import * as React from "react";

import { BaseButton, ITheme } from "@fluentui/react";

import {
  dataUniqueId,
  getInputMethodFromEvent,
  InputMethod,
} from "@1js/acui-common";
import { AppContext } from "@1js/acui-context";
import { checkExperiment, ribbonExperiments } from "@1js/acui-experiments";
import { iconNameToIconProps } from "@1js/acui-icons";
import { defaultKeytipExecute, deriveTabKeytip } from "@1js/acui-keytip";
import { AppThemeConsumer } from "@1js/acui-theme";
import { AppTooltipHost } from "@1js/acui-tooltip";

import { getPlaceHolderStyles } from "../SelectedPivotIndicator";
import { IPivotHeaderProps } from "./PivotHeader.Props";
import {
  getClassNames,
  getDefaultGhostPivotLabelStyles,
  getPivotHeaderButtonStyles,
  getSelectedPivotHeaderButtonStyles,
  getVerticalSeparatorClassNames,
  pivotWrapperStyles,
} from "./PivotHeader.styles";

export const pivotHeaderWrapperIDSuffix = "-wrapper";
export const pivotHeaderGhostElementClassname = "ghostPivotElement";

export class PivotHeader extends React.PureComponent<IPivotHeaderProps, {}> {
  private tooltipHost: AppTooltipHost | undefined;
  private baseButton = React.createRef<BaseButton>();

  // This allows us to access AppContext directly from this.context
  static contextType: any = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  public render() {
    const {
      selectedPivotHeader,
      title,
      styles,
      id,
      ariaControls,
      ariaPosInSet,
      ariaSetSize,
      ariaLabel,
      disabled,
      keytipProps,
      displayTabIndicatorPlaceholder,
      hideLabel,
      icon,
      automationType,
    } = this.props;

    const classNames = getClassNames(styles);

    const pivotHeader = (
      <AppThemeConsumer>
        {(theme: ITheme) => {
          const buttonStyles = selectedPivotHeader
            ? getSelectedPivotHeaderButtonStyles(
                theme,
                this.props.appColors,
                styles
              )
            : getPivotHeaderButtonStyles(theme, styles);
          return (
            <div
              className={classNames.wrapper}
              id={id + pivotHeaderWrapperIDSuffix}
              style={pivotWrapperStyles}
            >
              <BaseButton
                ref={this.baseButton}
                role="tab"
                id={id}
                text={!hideLabel ? title : undefined}
                iconProps={iconNameToIconProps(icon)}
                styles={buttonStyles}
                disabled={disabled}
                data-automation-type={automationType}
                data-unique-id={dataUniqueId(this.props)}
                aria-selected={selectedPivotHeader}
                aria-controls={ariaControls}
                aria-posinset={ariaPosInSet}
                aria-setsize={ariaSetSize}
                aria-label={ariaLabel}
                onBlur={this.onBlur}
                onClick={this.onClick}
                onFocus={this.onFocus}
                onMouseDown={this.onMouseDown}
                onMouseEnter={this.onMouseEnter}
                onMouseLeave={this.onMouseLeave}
                // onPivotHeaderExecute with new tab header styles sets the selected
                // tab indicator to be the width of the label (not full tab width as before).
                // Both keyboard focus and onMouseDown execute call it. With the latter, after mouse up
                // we want to be in the same state as hover, so we re-run the logic for onMouseEnter to
                // restore state we lose in the onMouseDown handler
                onMouseUp={this.onMouseUp}
                keytipProps={deriveTabKeytip(keytipProps, this.keytipExecute)}
                allowDisabledFocus={true}
              />
              {
                // This element reserves the space for a bolded label, so that it can grow without shifting the container
                // unless explicitly overwritten.
                <span
                  style={getDefaultGhostPivotLabelStyles(
                    styles && styles.ghostPivotLabel
                  )}
                  className={pivotHeaderGhostElementClassname}
                >
                  {title}
                </span>
              }
              {displayTabIndicatorPlaceholder && (
                <div
                  style={getPlaceHolderStyles(
                    theme,
                    this.props.appColors,
                    styles && styles.indicator
                  )}
                />
              )}
            </div>
          );
        }}
      </AppThemeConsumer>
    );
    return this.addWrapperIfVerticalSeparatorPresent(pivotHeader);
  }

  private addWrapperIfVerticalSeparatorPresent(pivotHeader: JSX.Element) {
    return this.props.displayTabVerticalSeparator ? (
      <div style={{ display: "inherit" }}>
        <div className={getVerticalSeparatorClassNames()} />
        {pivotHeader}
      </div>
    ) : (
      pivotHeader
    );
  }

  public componentDidMount() {
    if (
      this.props.hideLabel &&
      this.context.setTooltipElement &&
      this.context.updateTooltipsHidden
    ) {
      this.tooltipHost = new AppTooltipHost({
        updateTooltipsHidden: this.context.updateTooltipsHidden,
        setTooltipElement: this.context.setTooltipElement,
        refElement: this.baseButton,
        controlId: this.props.id || "",
        controlLabel: this.props.title || "",
        tooltipDirection: this.props.tooltipDirection,
        performSetOnNextFrame: !!checkExperiment(
          this.context,
          ribbonExperiments.delayRenderTooltipsIsEnabled
        ),
      });
      this.tooltipHost.registerTooltipHostActions();
    }
  }

  public componentWillUnmount() {
    if (this.tooltipHost) {
      this.tooltipHost.unregisterTooltipHostActions();
    }
  }

  private keytipExecute = (executeTarget: HTMLElement) => {
    defaultKeytipExecute(executeTarget, this.onPivotHeaderExecuteFromKeytip);
  };

  private onMouseDown = (ev: React.MouseEvent<BaseButton>) => {
    const { onMouseDown, id, onPivotHeaderExecute } = this.props;
    if (onMouseDown) {
      onMouseDown(id);
    }
    if (onPivotHeaderExecute) {
      ev.preventDefault(); // prevents mouse causing element focus (and corresponding css styles)
    }
  };

  private onClick = (ev: React.MouseEvent<BaseButton>) => {
    const { id, onPivotHeaderExecute, onClick } = this.props;
    if (onPivotHeaderExecute) {
      onPivotHeaderExecute(
        id,
        {
          inputMethod: getInputMethodFromEvent(ev),
        },
        ev && ev.timeStamp
      );
      ev.preventDefault();
    }
    if (onClick) {
      onClick(id);
    }
  };

  private onMouseUp = (_ev: React.MouseEvent<BaseButton>) => {
    const { id, onMouseUp } = this.props;
    if (onMouseUp) {
      onMouseUp(id);
    }
  };

  private onMouseEnter = (_ev: React.MouseEvent<BaseButton>) => {
    const { id, onMouseEnter } = this.props;
    if (onMouseEnter) {
      onMouseEnter(id);
    }
  };

  private onMouseLeave = (_ev: React.MouseEvent<BaseButton>) => {
    const { id, onMouseLeave } = this.props;
    if (onMouseLeave) {
      onMouseLeave(id);
    }
  };

  private onBlur = (_ev: React.FocusEvent<BaseButton>) => {
    const { id, onBlur } = this.props;
    if (onBlur) {
      onBlur(id);
    }
  };

  private onFocus = () => {
    const { id, disabled, onFocus } = this.props;
    if (!disabled && onFocus) {
      onFocus(id);
    }
  };

  private onPivotHeaderExecuteFromKeytip = () => {
    if (this.props.onPivotHeaderExecute) {
      this.props.onPivotHeaderExecute(this.props.id, {
        inputMethod: InputMethod.Keyboard,
      });
    }
  };
}
